import type { TFunction } from "i18next";
import type { RagIngestionConfirmation } from "../services/ragDocumentApi";
import { mapConfirmEnqueueFailures } from "../services/ragDocumentApi";

function lifecycleStatusLabel(status: string, t: TFunction): string {
  switch (status.trim().toLowerCase()) {
    case "processed":
      return t("opva.dmDocumentStatusProcessed", { defaultValue: "Processed" });
    case "processing":
    case "chunking":
    case "embedding":
      return t("opva.dmDocumentStatusProcessing", { defaultValue: "Processing" });
    case "failed":
      return t("opva.dmDocumentStatusFailed", { defaultValue: "Failed" });
    case "rolled_back":
      return t("opva.dmDocumentStatusRolledBack", { defaultValue: "Rolled back" });
    case "uploaded":
      return t("opva.dmErrStatusUploaded", { defaultValue: "Awaiting confirmation" });
    default:
      return status;
  }
}

/** Map API / worker confirm-ingestion errors to plain-language copy. */
export function friendlyConfirmIngestionError(
  raw: string,
  t: TFunction
): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return t("opva.dmErrConfirmGeneric", {
      defaultValue: "Processing could not be started. Please try again.",
    });
  }

  const lower = trimmed.toLowerCase();

  if (
    lower.includes("background processing unavailable") ||
    lower.includes("celery broker unavailable") ||
    lower.includes("could not start ingestion worker")
  ) {
    return t("opva.dmBackgroundProcessingUnavailable", {
      defaultValue:
        "Background processing is unavailable. Try again later or contact your administrator.",
    });
  }

  if (lower.includes("rag worker unavailable")) {
    return t("opva.dmErrWorkerUnavailable", {
      defaultValue:
        "The document processing service is not reachable. Check that it is running and try again.",
    });
  }

  const statusMatch = trimmed.match(
    /document not ready for ingestion \(status=(\w+)\)/i
  );
  if (statusMatch) {
    const status = statusMatch[1].toLowerCase();
    if (status === "processed") {
      return t("opva.dmErrAlreadyProcessed", {
        defaultValue:
          "This document has already finished processing. Refresh the page to see the latest status.",
      });
    }
    if (
      status === "processing" ||
      status === "chunking" ||
      status === "embedding"
    ) {
      return t("opva.dmErrAlreadyProcessing", {
        defaultValue:
          "This document is already being processed. Open the Upload tab to follow progress.",
      });
    }
    if (status === "rolled_back") {
      return t("opva.dmErrRolledBackRefresh", {
        defaultValue:
          "Refresh the document list, then use Retry processing if this document still shows Rolled back.",
      });
    }
    const label = lifecycleStatusLabel(status, t);
    return t("opva.dmErrNotReadyForIngestion", {
      status: label,
      defaultValue:
        "This document cannot be processed right now (current status: {{status}}). Refresh the page and try again.",
    });
  }

  if (lower.includes("metadata detection still in progress")) {
    return t("opva.dmErrMetadataInProgress", {
      defaultValue:
        "Metadata is still being detected for this file. Wait for detection to finish, then try again.",
    });
  }

  if (lower.includes("metadata detection failed")) {
    return t("opva.dmErrMetadataFailed", {
      defaultValue:
        "Metadata detection failed for this file. Review company, product, and model on the upload screen, then try again.",
    });
  }

  if (lower === "not found" || lower.includes("document not found")) {
    return t("opva.dmErrDocumentNotFound", {
      defaultValue:
        "This document could not be found. Refresh the document list and try again.",
    });
  }

  if (lower === "no worker result") {
    return t("opva.dmErrNoWorkerResult", {
      defaultValue:
        "The processing service did not accept this document. Try again in a moment.",
    });
  }

  if (
    lower === "could not start ingestion" ||
    lower.includes("could not start ingestion")
  ) {
    return t("opva.dmErrCouldNotStartProcessing", {
      defaultValue:
        "Processing could not be started. Make sure the ingestion worker is running and try again.",
    });
  }

  if (lower === "missing document_id") {
    return t("opva.dmErrConfirmGeneric", {
      defaultValue: "Processing could not be started. Please try again.",
    });
  }

  return trimmed;
}

export function formatConfirmEnqueueFailureLine(
  fileName: string,
  rawError: string,
  t: TFunction
): string {
  const message = friendlyConfirmIngestionError(rawError, t);
  return t("opva.dmConfirmFailureLine", {
    fileName,
    message,
    defaultValue: "{{fileName}} — {{message}}",
  });
}

export function mapConfirmEnqueueFailuresForDisplay(
  confs: RagIngestionConfirmation[],
  t: TFunction
): { fileName: string; error: string }[] {
  return mapConfirmEnqueueFailures(confs).map((f) => ({
    fileName: f.fileName,
    error: friendlyConfirmIngestionError(f.error, t),
  }));
}

export function friendlyConfirmIngestionHttpError(
  data: unknown,
  status: number,
  t: TFunction,
  fallback: string
): string {
  if (status === 403) {
    return t("opva.dmForbidden", {
      defaultValue: "Org Admin role is required to update documents.",
    });
  }
  if (status === 503) {
    return t("opva.dmBackgroundProcessingUnavailable", {
      defaultValue:
        "Background processing is unavailable. Try again later or contact your administrator.",
    });
  }
  if (data && typeof data === "object" && "message" in data) {
    const msg = (data as { message?: string }).message;
    if (msg?.trim()) {
      return friendlyConfirmIngestionError(msg, t);
    }
  }
  return fallback;
}

/** Plain-language copy for ingestion job error_message values from the worker. */
export function friendlyIngestionJobError(
  raw: string | undefined,
  t: TFunction
): string {
  const trimmed = raw?.trim() ?? "";
  if (
    !trimmed ||
    trimmed === "INGESTION_REMOVED_EMPTY_CONTENT" ||
    trimmed.toLowerCase().includes("no readable text and was removed")
  ) {
    return t("opva.dmIngestionRemovedEmptyContent", {
      defaultValue:
        "This file has no readable text and was removed. Upload a non-empty document.",
    });
  }
  if (trimmed.toLowerCase().includes("could not be processed and was removed")) {
    return trimmed;
  }
  if (trimmed.toLowerCase().includes("were empty") || trimmed.toLowerCase().includes("nothing to index")) {
    return t("opva.dmIngestionRemovedEmptyContent", {
      defaultValue:
        "This file has no readable text and was removed. Upload a non-empty document.",
    });
  }
  if (trimmed.toLowerCase().includes("produced 0 chunks")) {
    return t("opva.dmIngestionRemovedEmptyContent", {
      defaultValue:
        "This file has no readable text and was removed. Upload a non-empty document.",
    });
  }
  if (trimmed.toLowerCase().includes("processing timed out")) {
    return t("opva.dmIngestionTimedOut", {
      defaultValue:
        "Processing took too long and was rolled back. Try again or contact your administrator.",
    });
  }
  return trimmed;
}
